import React, { useEffect, useState } from 'react';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { Controller } from 'react-hook-form';

import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { useMediaQuery } from 'src/shared/hooks';
import { Address, Organization } from 'src/shared/types/user';

import { addToast, Checkbox } from 'src/shared/components/ui';
import {
  ButtonV2,
  GooglePlacesAutocompleteInput,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Typography
} from 'src/shared/components/ui';

import { useOrganizationAddressForm } from '../hooks/useOrganizationAddressForm';
import { useUpdateOrganizationAddress } from '../hooks/useUpdateOrganizationAddress';

interface OrganizationAddressFormModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  selectedAddress: (Address & { index: number }) | null;
  selectedOrganization: Organization;
  onSuccess?: (name: any) => void;
}

const OrganizationAddressFormModal: React.FC<
  OrganizationAddressFormModalProps
> = ({
  isOpen,
  onOpenChange,
  selectedAddress,
  selectedOrganization,
  onSuccess
}) => {
  const isMobile = useMediaQuery();
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useOrganizationAddressForm(
    selectedAddress
      ? {
          ...selectedAddress,
          is_default: selectedAddress.index === 0 ? true : false
        }
      : undefined
  );

  const setSelectedOrganization = useAuthStore(
    (state) => state.setSelectedOrganization
  );
  const organizationAddressMutation = useUpdateOrganizationAddress();
  const [addresssPlace, setAddresssPlace] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      if (selectedAddress) {
        reset({
          ...selectedAddress,
          is_default: selectedAddress.index === 0 ? true : false
        });
      }
    }
  }, [reset, selectedAddress, isOpen]);

  const handlePlaceSelect = async (place: any) => {
    if (!place || !place.value || !place.value.place_id) return;

    try {
      const results = await geocodeByPlaceId(place.value.place_id);
      const addressComponents = results[0].address_components;

      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let zipCode = '';
      let country = '';

      addressComponents.forEach((component: any) => {
        const types = component.types;

        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
        if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }
        if (types.includes('country')) {
          country = component.long_name;
        }
      });

      // Update form fields using setValue
      const line1 = `${streetNumber} ${route}`.trim();

      if (line1) setValue('line_1', line1);
      if (city) setValue('city', city);
      if (state) setValue('state', state);
      if (zipCode) setValue('zip_code', zipCode);
      if (country) setValue('country', country);
    } catch (error) {
      console.error('Error geocoding address:', error);
    }
  };

  const onSubmit = (data: Address) => {
    const addressName = data.name;
    let addresses: Address[] = [];

    // if new address
    if (!selectedAddress) {
      addresses = [
        ...(selectedOrganization.addresses as Address[]),
        { ...data }
      ];

      if (data.is_default) {
        addresses = [
          { ...data },
          ...(selectedOrganization.addresses as Address[])
        ];
      }
    }

    // if update address
    if (selectedAddress) {
      addresses = selectedOrganization.addresses?.map((address, i) => {
        if (i === selectedAddress.index) {
          return { ...data };
        }

        return address;
      }) as Address[];

      if (data.is_default) {
        const updatedAddress = addresses[selectedAddress.index];

        addresses.splice(selectedAddress.index, 1);

        addresses.unshift(updatedAddress);
      }
    }

    organizationAddressMutation.mutate(
      { addresses, id: selectedOrganization.id },
      {
        onError: () => {
          addToast({
            color: 'danger',
            title: `Address could not be ${selectedAddress ? 'updated' : 'added'}.`
          });
        },
        onSuccess: (data: Organization) => {
          onOpenChange(false);
          setSelectedOrganization({
            ...selectedOrganization,
            addresses: data.addresses
          });
          onSuccess && onSuccess(addressName);
          addToast({
            color: 'success',
            title: `Address has been successfully ${
              selectedAddress ? 'updated' : 'added'
            }.`
          });
        }
      }
    );
  };

  const onRemoveAddress = () => {
    if (selectedOrganization.addresses) {
      let addresses: Address[] = [];

      addresses = selectedOrganization.addresses.filter(
        (_, i) => i !== selectedAddress?.index
      );

      organizationAddressMutation.mutate(
        { addresses, id: selectedOrganization.id },
        {
          onError: () => {
            addToast({
              color: 'danger',
              title: 'Address could not be removed.'
            });
          },
          onSuccess: (data: Organization) => {
            onOpenChange(false);
            setSelectedOrganization({
              ...selectedOrganization,
              addresses: data.addresses
            });
            addToast({
              color: 'success',
              title: 'Address has been successfully removed.'
            });
          }
        }
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      scrollBehavior={isMobile ? 'normal' : 'inside'}
      hideCloseButton
      placement={isMobile ? 'top-center' : 'auto'}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 p-5 border-b border-gray-4">
              {selectedAddress ? 'Edit address' : 'Add address'}
            </ModalHeader>
            <ModalBody className="p-5">
              <form
                className="flex flex-col gap-9"
                id="organization-address-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div>
                  <Input
                    label="Name"
                    errorMessage={errors.name?.message}
                    isInvalid={!!errors.name}
                    {...register('name')}
                  />
                  <Controller
                    name="is_default"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        isSelected={value}
                        onValueChange={onChange}
                        className="mt-2"
                      >
                        <Typography variant="body-1" as="span" weight="medium">
                          Set as default
                        </Typography>
                      </Checkbox>
                    )}
                  />
                </div>

                <div>
                  <Typography variant="h6" as="span" weight="bold">
                    Address
                  </Typography>

                  <div className="grid grid-cols-2 mt-3 gap-5">
                    <div className="col-span-2">
                      <GooglePlacesAutocompleteInput
                        value={addresssPlace}
                        onChange={handlePlaceSelect}
                        onInputChange={setAddresssPlace}
                        label="Search Address"
                      />
                    </div>
                    <Controller
                      name="line_1"
                      control={control}
                      render={({ field }) => (
                        <Input
                          className="col-span-2"
                          label="Address line 1"
                          errorMessage={errors.line_1?.message}
                          isInvalid={!!errors.line_1}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="line_2"
                      control={control}
                      render={({ field }) => (
                        <Input
                          className="col-span-2"
                          label="Address line 2"
                          errorMessage={errors.line_2?.message}
                          isInvalid={!!errors.line_2}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="City"
                          errorMessage={errors.city?.message}
                          isInvalid={!!errors.city}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="State"
                          errorMessage={errors.state?.message}
                          isInvalid={!!errors.state}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="zip_code"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Zipcode"
                          errorMessage={errors.zip_code?.message}
                          isInvalid={!!errors.zip_code}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Country"
                          errorMessage={errors.country?.message}
                          isInvalid={!!errors.country}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <Typography variant="h6" as="span" weight="bold">
                    Contact
                  </Typography>

                  <div className="grid grid-cols-2 mt-3 gap-5">
                    <Input
                      className="col-span-2"
                      label="Contact name"
                      errorMessage={errors.contact_name?.message}
                      isInvalid={!!errors.contact_name}
                      {...register('contact_name')}
                    />
                    <Input
                      className="col-span-2"
                      label="Contact number"
                      errorMessage={errors.contact_phone?.message}
                      isInvalid={!!errors.contact_phone}
                      {...register('contact_phone')}
                    />
                    <Input
                      className="col-span-2"
                      label="Contact email"
                      type="email"
                      errorMessage={errors.contact_email?.message}
                      isInvalid={!!errors.contact_email}
                      {...register('contact_email')}
                    />
                  </div>
                </div>
              </form>
            </ModalBody>
            <ModalFooter className="justify-start border-t border-gray-4 p-5">
              {selectedAddress && (
                <ButtonV2
                  color="secondary"
                  onPress={onRemoveAddress}
                  isDisabled={organizationAddressMutation.isPending}
                  className="mobile:w-full"
                >
                  Delete address
                </ButtonV2>
              )}
              <ButtonV2
                color="secondary"
                onPress={() => onClose()}
                isDisabled={organizationAddressMutation.isPending}
                className="mobile:w-full"
              >
                Close
              </ButtonV2>
              <ButtonV2
                form="organization-address-form"
                type="submit"
                color="primary"
                isDisabled={organizationAddressMutation.isPending}
                isLoading={organizationAddressMutation.isPending}
                className="mobile:w-full"
              >
                Save
              </ButtonV2>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default OrganizationAddressFormModal;
